/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'primereact/dropdown';

interface PaginationInfo {
  currentPage: number;
  rows: number;
  pages: number;
}

type MutationFn = (query?: { page?: number; limit?: number; runs?: number }) => Promise<unknown>;

export const usePagination = (paginationInfo: PaginationInfo, mutationFn: MutationFn) => {
  const paginatorTemplate = useMemo(() => {
    const { currentPage, rows, pages } = paginationInfo;

    return {
      layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
      FirstPageLink: () => {
        const disabled = currentPage === 1;
        return (
          <button
            type="button"
            className={classNames('p-paginator-prev p-paginator-element p-link border-round', {
              'p-disabled': disabled
            })}
            onClick={() => {
              mutationFn({ page: 1, limit: paginationInfo.rows });
            }}
            disabled={disabled}
          >
            <span className="p-3">
              <i className="pi pi-angle-double-left" />
            </span>
          </button>
        );
      },
      PrevPageLink: () => {
        const disabled = currentPage === 1;
        return (
          <button
            type="button"
            className={classNames('p-paginator-prev p-paginator-element p-link border-round', {
              'p-disabled': disabled
            })}
            onClick={() => {
              mutationFn({ page: currentPage - 1, limit: paginationInfo.rows });
            }}
            disabled={disabled}
          >
            <span className="p-3">
              <i className="pi pi-angle-left" />
            </span>
          </button>
        );
      },
      NextPageLink: () => {
        const disabled = pages === 1 || currentPage === pages;
        return (
          <button
            type="button"
            className={classNames('p-paginator-next p-paginator-element p-link border-round', {
              'p-disabled': disabled
            })}
            onClick={() => {
              mutationFn({ page: currentPage + 1, limit: paginationInfo.rows });
            }}
            disabled={disabled}
          >
            <span className="p-3">
              <i className="pi pi-angle-right" />
            </span>
          </button>
        );
      },
      LastPageLink: () => {
        const disabled = pages === 1 || currentPage === pages;
        return (
          <button
            type="button"
            className={classNames('p-paginator-next p-paginator-element p-link border-round', {
              'p-disabled': disabled
            })}
            onClick={() => {
              mutationFn({ page: pages, limit: rows });
            }}
            disabled={disabled}
          >
            <span className="p-3">
              <i className="pi pi-angle-double-right" />
            </span>
          </button>
        );
      },
      PageLinks: () => {
        const pageIndex = Array.from({ length: pages }, (_never, idx) => {
          const index = idx + 1;
          return (
            <button
              key={idx}
              type="button"
              className={classNames(
                'p-paginator-page p-paginator-element p-link p-paginator-page-start p-paginator-page-end',
                { 'p-highlight': index === currentPage }
              )}
              onClick={() => {
                mutationFn({ page: index, limit: rows });
              }}
            >
              {index}
            </button>
          );
        });
        return (
          <>
            {pageIndex}
            {paginationInfo.pages > 10 && <span className="mx-2">...</span>}
          </>
        );
      },
      RowsPerPageDropdown: () => {
        const dropdownOptions = [
          { label: 10, value: 10 },
          { label: 20, value: 20 },
          { label: 30, value: 30 },
          { label: 50, value: 50 }
        ];

        return (
          <Dropdown
            value={paginationInfo.rows}
            options={dropdownOptions}
            onChange={(e) => {
              mutationFn({ page: 1, limit: e.value });
            }}
          />
        );
      }
    };
  }, [paginationInfo, mutationFn]);

  return {
    paginatorTemplate
  };
};
